import * as lucideIcons from 'lucide-vue-next'
import type { LucideIcon as LucideIconType } from 'lucide-vue-next'

const DIRECT_ICON_ALIASES: Record<string, string | string[]> = {
  account: 'user',
  'account-plus': 'user-plus',
  'account-multiple': 'users',
  'account-group': 'users',
  person: 'user',
  email: 'mail',
  cellphone: 'smartphone',
  application: 'app-window',
  'file-document': 'file-text',
  close: 'x',
  'close-circle': 'circle-x',
  'close-box': 'square-x',
  delete: ['trash-2', 'trash'],
}

const TOKEN_ICON_ALIASES: Record<string, string> = {
  account: 'user',
  email: 'mail',
  close: 'x',
}

function isPascalCase(value: string) {
  return /^[A-Z][A-Za-z0-9]*$/.test(value)
}

function toKebabCase(value: string) {
  return value
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()
}

function normalizeIconName(value: string) {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .toLowerCase()
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-')
}

function uniquePush(target: string[], value?: string | null) {
  if (!value) return
  if (!target.includes(value)) target.push(value)
}

function uniquePushMany(target: string[], value?: string | string[] | null) {
  if (!value) return
  if (Array.isArray(value)) {
    for (const item of value) uniquePush(target, item)
    return
  }
  uniquePush(target, value)
}

function replaceTokenSegments(iconName: string, aliases: Record<string, string>) {
  return iconName
    .split('-')
    .map((segment) => aliases[segment] || segment)
    .join('-')
}

function stripModifierTokens(iconName: string) {
  const stripped = iconName
    .split('-')
    .filter((token) => !['outline', 'filled', 'fill', 'solid', 'variant'].includes(token))
    .join('-')

  return stripped || iconName
}

function moveShapeSuffixToPrefix(iconName: string) {
  const parts = iconName.split('-')
  if (parts.length < 2) return iconName

  const suffix = parts[parts.length - 1]
  if (!['circle', 'square', 'triangle', 'octagon'].includes(suffix)) return iconName

  return [suffix, ...parts.slice(0, -1)].join('-')
}

function collapseNumericHyphens(iconName: string) {
  return iconName.replace(/-([0-9])/g, '$1').replace(/([0-9])-/g, '$1')
}

const lucideIconsByKebabName: Record<string, LucideIconType> = {}

for (const [exportName, icon] of Object.entries(lucideIcons)) {
  if (!isPascalCase(exportName) || !icon) continue
  lucideIconsByKebabName[toKebabCase(exportName)] = icon as LucideIconType
}

function buildKebabCandidates(iconName: string) {
  const normalized = normalizeIconName(iconName)
  const rootCandidates: string[] = []
  const candidates: string[] = []
  const queue: string[] = []
  const visited = new Set<string>()

  uniquePush(rootCandidates, normalized)
  if (normalized.startsWith('mdi-')) {
    uniquePush(rootCandidates, normalized.slice(4))
  }
  if (normalized.startsWith('lucide-')) {
    uniquePush(rootCandidates, normalized.slice(7))
  }

  for (const root of rootCandidates) uniquePush(queue, root)

  while (queue.length) {
    const current = queue.shift()
    if (!current || visited.has(current)) continue
    visited.add(current)
    uniquePush(candidates, current)

    uniquePush(queue, stripModifierTokens(current))
    uniquePush(queue, replaceTokenSegments(current, TOKEN_ICON_ALIASES))
    uniquePush(queue, moveShapeSuffixToPrefix(current))
    uniquePush(queue, collapseNumericHyphens(current))
    uniquePushMany(queue, DIRECT_ICON_ALIASES[current])
  }

  // Prevent runaway expansion when users pass malformed icon strings.
  if (candidates.length > 50) return candidates.slice(0, 50)

  return candidates
}

export function resolveLucideIcon(iconName: string): LucideIconType | null {
  const trimmed = iconName?.trim()
  if (!trimmed) return null

  if (isPascalCase(trimmed)) {
    const exact = lucideIcons[trimmed as keyof typeof lucideIcons]
    if (exact) return exact
  }

  for (const candidate of buildKebabCandidates(trimmed)) {
    const icon = lucideIconsByKebabName[candidate]
    if (icon) return icon
  }

  return null
}
